<script lang="ts">
	import { onMount } from 'svelte';
	import { organizationsApi, type Organization } from '../api';
	import { userStore } from '../stores/user';
	import OrganizationDrawer from '../components/OrganizationDrawer.svelte';
	import ConfirmationDrawer from '../components/ConfirmationDrawer.svelte';
	import OrganizationCard from '../components/OrganizationCard.svelte';
	import OrganizationDetailsDrawer from '../components/OrganizationDetailsDrawer.svelte';
	import LoadingSpinner from '../components/LoadingSpinner.svelte';
	import ErrorMessage from '../components/ErrorMessage.svelte';
	import EmptyState from '../components/EmptyState.svelte';
	import PageHeader from '../components/PageHeader.svelte';

	let organizations = $state<Organization[]>([]);
	let ownedOrganizations = $state<Organization[]>([]);
	let loading = $state(true);
	let error = $state<string | null>(null);
	let isOrganizationDrawerOpen = $state(false);
	let selectedOrganization = $state<Organization | null>(null);
	let isConfirmationDrawerOpen = $state(false);
	let organizationToDelete = $state<Organization | null>(null);
	let isDeleting = $state(false);
	let activeTab = $state<'all' | 'owned'>('all');
	let isViewDetailsDrawerOpen = $state(false);
	let organizationToView = $state<Organization | null>(null);

	function openCreateModal() {
		selectedOrganization = null;
		isOrganizationDrawerOpen = true;
	}

	function openEditModal(organization: Organization) {
		selectedOrganization = organization;
		isOrganizationDrawerOpen = true;
		// Close details drawer if it's open to avoid having both open at once
		if (isViewDetailsDrawerOpen) {
			isViewDetailsDrawerOpen = false;
			organizationToView = null;
		}
	}

	function closeOrganizationModal() {
		isOrganizationDrawerOpen = false;
		selectedOrganization = null;
	}

	function openDeleteConfirmation(organization: Organization) {
		organizationToDelete = organization;
		isConfirmationDrawerOpen = true;
	}

	function closeDeleteConfirmation() {
		isConfirmationDrawerOpen = false;
		organizationToDelete = null;
	}

	function openViewDetails(organization: Organization) {
		organizationToView = organization;
		isViewDetailsDrawerOpen = true;
	}

	function closeViewDetails() {
		isViewDetailsDrawerOpen = false;
		organizationToView = null;
	}

	async function handleDeleteConfirm() {
		if (!organizationToDelete) return;

		try {
			isDeleting = true;
			const organizationId = organizationToDelete.id;
			await organizationsApi.deleteOrganization(organizationId);

			// Remove the deleted organization from both lists
			organizations = organizations.filter((org) => org.id !== organizationId);
			ownedOrganizations = ownedOrganizations.filter((org) => org.id !== organizationId);

			closeDeleteConfirmation();
		} catch (err) {
			console.error('Error deleting organization:', err);
			error = err instanceof Error ? err.message : 'Failed to delete organization';
		} finally {
			isDeleting = false;
		}
	}

	async function handleOrganizationSaved() {
		closeOrganizationModal();
		await loadOrganizations();
	}

	async function loadOrganizations() {
		try {
			loading = true;
			error = null;

			// Load both user organizations and owned organizations in parallel
			const [userOrgs, ownedOrgs] = await Promise.all([
				organizationsApi.getUserOrganizations(),
				organizationsApi.getOwnedOrganizations()
			]);

			organizations = userOrgs;
			ownedOrganizations = ownedOrgs;
		} catch (err) {
			console.error('Error loading organizations:', err);
			error = err instanceof Error ? err.message : 'Failed to load organizations';
		} finally {
			loading = false;
		}
	}

	function isOwner(organization: Organization): boolean {
		return organization.ownerId === $userStore?.id;
	}

	function canEdit(organization: Organization): boolean {
		return isOwner(organization);
	}

	onMount(() => {
		console.log('Organizations page mounted!');
		loadOrganizations();
	});

	const currentOrganizations = $derived(activeTab === 'all' ? organizations : ownedOrganizations);
</script>

<div class="container mx-auto px-4 py-8">
	<!-- Header -->
	<PageHeader
		title="Organizations"
		description="Manage your sailing clubs and organizations"
		actionText="Create Organization"
		onAction={openCreateModal}
	/>

	<!-- Tabs -->
	<div class="mt-6 border-b border-gray-200">
		<nav class="-mb-px flex space-x-8" aria-label="Tabs">
			<button
				onclick={() => (activeTab = 'all')}
				class="whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors {activeTab ===
				'all'
					? 'border-blue-500 text-blue-600'
					: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}"
			>
				All Organizations ({organizations.length})
			</button>
			<button
				onclick={() => (activeTab = 'owned')}
				class="whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors {activeTab ===
				'owned'
					? 'border-blue-500 text-blue-600'
					: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}"
			>
				Organizations I Own ({ownedOrganizations.length})
			</button>
		</nav>
	</div>

	<!-- Loading State -->
	{#if loading}
		<LoadingSpinner />
	{/if}

	<!-- Error State -->
	{#if error}
		<ErrorMessage message={error} onRetry={loadOrganizations} />
	{/if}

	<!-- Organizations Grid -->
	{#if !loading && !error}
		<div class="mt-8">
			{#if currentOrganizations.length === 0}
				<EmptyState
					title={activeTab === 'all' ? 'No organizations found' : 'No organizations owned'}
					description={activeTab === 'all'
						? "You haven't joined any organizations yet."
						: "You haven't created any organizations yet."}
					actionText="Create Your First Organization"
					onAction={openCreateModal}
				/>
			{:else}
				<div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{#each currentOrganizations as organization (organization.id)}
						<OrganizationCard
							{organization}
							isOwner={isOwner(organization)}
							onEdit={canEdit(organization) ? openEditModal : undefined}
							onDelete={canEdit(organization) ? openDeleteConfirmation : undefined}
							onViewDetails={openViewDetails}
						/>
					{/each}
				</div>
			{/if}
		</div>
	{/if}
</div>

<!-- Organization Create/Edit Drawer -->
<OrganizationDrawer
	isOpen={isOrganizationDrawerOpen}
	organization={selectedOrganization}
	onClose={closeOrganizationModal}
	onSaved={handleOrganizationSaved}
/>

<!-- Delete Confirmation Drawer -->
<ConfirmationDrawer
	isOpen={isConfirmationDrawerOpen}
	title="Delete Organization"
	message="Are you sure you want to delete '{organizationToDelete?.name}'? This action cannot be undone and will remove all members."
	confirmText={isDeleting ? 'Deleting...' : 'Delete Organization'}
	confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
	onConfirm={handleDeleteConfirm}
	onCancel={closeDeleteConfirmation}
/>

<!-- Organization Details Drawer -->
<OrganizationDetailsDrawer
	isOpen={isViewDetailsDrawerOpen}
	organization={organizationToView}
	isOwner={organizationToView ? isOwner(organizationToView) : false}
	onClose={closeViewDetails}
	onEdit={openEditModal}
	onDelete={openDeleteConfirmation}
/>
